import { useQuery } from "@tanstack/solid-query";
import type { TovProfileResponse } from "contracts/api/client-tov/responses";
import type { TovVersionResponse } from "contracts/api/client-tov/version.responses";
import { createEffect, createSignal } from "solid-js";
import { QUERY_ROOT } from "../../enums/shell";
import type { TovSectionKey } from "../../enums/tov";
import { TOV_MAPPING_STATE, TOV_SECTION_KEY } from "../../enums/tov";
import { api } from "../../store/api";
import { errorMessage } from "../../store/api-client";
import { SOURCE_POLL_MS } from "../../store/pollCadence";
import { queryClient } from "../../store/query";
import { isTovQuery } from "../../store/query-keys";
import type { CardEditProps, EditValues } from "../tov/EditForms";
import { sourceTypeFor } from "../tov/SourcesPanel";
import { isCascadeActive, mapInFlight } from "../tov/statusLabels";
import { createMapAttemptStore } from "../tov/useMapAttempt";

/**
 * TOV authoring state for one Client: profiles, versions, sources, definition
 * edits. Keyed purely by clientId, so it drives both the Signal Scan "Manage"
 * surface and the in-place playground drawer — TOV is Client-scoped, and a
 * playground workspace is a Client. The caller supplies clientId/clientName as
 * accessors (from a website store, a playground workspace, wherever).
 *
 * Data flows through TanStack Query on the same tov-* keys the read views use,
 * so one invalidation after each write refreshes every surface.
 */
export function useBrandAuthoring(props: {
  clientId: () => string | undefined;
  clientName: () => string;
}) {
  const clientId = props.clientId;
  const clientName = props.clientName;

  const [profileId, setProfileId] = createSignal<string | null>(null);
  const [versionId, setVersionId] = createSignal<string | null>(null);
  const [error, setError] = createSignal("");
  const [mapNotice, setMapNotice] = createSignal("");
  const [mapPending, setMapPending] = createSignal(false);
  const [activeTab, setActiveTab] = createSignal<TovSectionKey>(
    TOV_SECTION_KEY.VOICE,
  );
  const [uploadBusy, setUploadBusy] = createSignal(false);

  const profiles = useQuery(() => ({
    queryKey: [QUERY_ROOT.TOV_PROFILES, clientId()],
    queryFn: () => api.tov.listProfiles(clientId() as string),
    enabled: clientId() !== undefined,
  }));
  const versions = useQuery(() => ({
    queryKey: [QUERY_ROOT.TOV_VERSIONS, clientId(), profileId()],
    queryFn: () =>
      api.tov.listVersions(clientId() as string, profileId() as string),
    enabled: clientId() !== undefined && profileId() !== null,
  }));
  const version = useQuery(() => ({
    queryKey: [QUERY_ROOT.TOV_VERSION, clientId(), versionId() ?? undefined],
    queryFn: () =>
      api.tov.getVersion(clientId() as string, versionId() as string),
    enabled: clientId() !== undefined && versionId() !== null,
  }));
  // Same key (and poll cadence) as SourcesPanel, so the map-in-flight state
  // clears itself when the mapping run lands even with the Sources tab closed.
  const sources = useQuery(() => ({
    queryKey: [QUERY_ROOT.TOV_SOURCES, clientId(), versionId() ?? undefined],
    queryFn: () =>
      api.tov.listSources(clientId() as string, versionId() as string),
    enabled: clientId() !== undefined && versionId() !== null,
    refetchInterval: (current) =>
      isCascadeActive(current.state.data?.sources ?? [])
        ? SOURCE_POLL_MS
        : false,
  }));
  // The attempt log is the authoritative mapping state: a resumed run leaves
  // its sources marked failed until completion, so source status alone would
  // read as idle while the worker is executing.
  const mapAttempt = createMapAttemptStore(
    clientId,
    () => versionId() ?? undefined,
  );
  const mappingActive = () =>
    mapPending() ||
    mapAttempt.state() === TOV_MAPPING_STATE.RUNNING ||
    mapInFlight(sources.data?.sources ?? []);

  // Auto-select the first profile on load.
  createEffect(() => {
    const list = profiles.data?.profiles ?? [];
    if (profileId() === null && list[0]) {
      setProfileId(list[0].id);
      setVersionId(list[0].currentVersionId);
    }
  });
  // A profile has no currentVersionId until first activation — fall back to
  // its newest version so a reload doesn't land on "No version to show yet".
  createEffect(() => {
    const list = versions.data?.versions ?? [];
    if (versionId() === null && list[0]) {
      setVersionId(list[0].id);
    }
  });

  const profileName = () =>
    profiles.data?.profiles.find((profile) => profile.id === profileId())
      ?.name ?? "Profile";

  const invalidateTov = () =>
    queryClient.invalidateQueries({
      predicate: (query) => isTovQuery(query.queryKey),
    });

  const guard = async (action: () => Promise<void>) => {
    setError("");
    try {
      await action();
      // Every read surface (this workspace, Brand Guidelines, Scan summary)
      // caches TOV under tov-* keys; one invalidation refreshes them all.
      await invalidateTov();
    } catch (cause) {
      setError(errorMessage(cause));
    }
  };

  const uploadDocument = async (file: File) => {
    setError("");
    setUploadBusy(true);
    try {
      // Every client is auto-provisioned a profile + version at website
      // creation (backend ClientTovService.ensureDefaultProfile), and the load
      // effects above select it. A missing version is a real bug, not a state
      // to paper over by creating one here.
      const targetVersionId = versionId();
      if (!targetVersionId) {
        throw new Error("No brand voice version is selected to upload into");
      }
      await api.tov.uploadSource(clientId() ?? "", targetVersionId, file, {
        sourceType: sourceTypeFor(file),
        title: file.name,
      });
      await invalidateTov();
      setActiveTab("sources");
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setUploadBusy(false);
    }
  };

  const onPick = (event: { currentTarget: HTMLInputElement }) => {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = "";
    if (file) {
      void uploadDocument(file);
    }
  };

  // Version writes return the fresh version — seed the cache so the workspace
  // updates before the invalidation round-trip completes.
  const seedVersion = (fresh: TovVersionResponse) => {
    queryClient.setQueryData(
      [QUERY_ROOT.TOV_VERSION, clientId(), fresh.id],
      fresh,
    );
  };

  const selectProfile = (profile: TovProfileResponse) => {
    setProfileId(profile.id);
    setVersionId(profile.currentVersionId);
  };

  // Edit/delete handlers shared by every definition card. The backend rejects
  // edits to non-draft Versions, so editability is gated on draft status too.
  const editProps = (editable: boolean): CardEditProps => ({
    editable,
    onEdit: (path: string, id: string, body: EditValues) =>
      guard(async () => {
        seedVersion(
          await api.tov.updateDefinition(clientId() ?? "", path, id, body),
        );
      }),
    onDelete: (path: string, id: string) =>
      guard(async () => {
        seedVersion(await api.tov.deleteDefinition(clientId() ?? "", path, id));
      }),
  });

  return {
    clientId,
    clientName,
    profileId,
    setProfileId,
    versionId,
    setVersionId,
    error,
    setError,
    mapNotice,
    setMapNotice,
    mapPending,
    setMapPending,
    activeTab,
    setActiveTab,
    uploadBusy,
    setUploadBusy,
    profiles,
    versions,
    version,
    sources,
    mapAttempt,
    mappingActive,
    profileName,
    invalidateTov,
    guard,
    uploadDocument,
    onPick,
    seedVersion,
    selectProfile,
    editProps,
  };
}
